import {
  clamp,
  clamp01,
  createAdvancedVoice,
  createBodyResonatorBank,
  createNoiseBurst,
  hashSeed,
  scheduleSafeEnvelope
} from "./dspPrimitives";
import { getVariantModelProfile } from "./instrumentModelProfiles";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, ResonatorSpec } from "./types";

function controlNumber(input: AdvancedSynthesisInput, id: string, fallback: number): number {
  const value = input.controls[id];

  return typeof value === "number" ? clamp01(value) : fallback;
}

function makeLipReedCurve(context: BaseAudioContext, pressure: number): WaveShaperNode {
  const shaper = context.createWaveShaper();
  const curve = new Float32Array(1024);
  const drive = 2.8 + pressure * 11;

  for (let index = 0; index < curve.length; index += 1) {
    const x = (index / (curve.length - 1)) * 2 - 1;
    const reed = Math.tanh(x * drive);
    const folded = Math.sin(x * Math.PI * (1.5 + pressure * 1.5)) * 0.12;

    curve[index] = reed * 0.72 + folded * pressure;
  }

  shaper.curve = curve;
  shaper.oversample = "2x";

  return shaper;
}

function boreResonators(frequency: number, variantId?: string): ResonatorSpec[] {
  const longTube = variantId === "traditional_yidaki";

  return [
    { frequency, q: 5, gainDb: 4.6, type: "peaking" },
    { frequency: frequency * (2.02 + (longTube ? -0.03 : 0.02)), q: 3.6, gainDb: 3.4, type: "peaking" },
    { frequency: frequency * (3.08 + (longTube ? -0.04 : 0.04)), q: 2.6, gainDb: 2.6, type: "peaking" },
    { frequency: frequency * 4.2, q: 2.0, gainDb: 1.8, type: "peaking" }
  ];
}

function formantFrequencies(mouthX: number, mouthY: number): Array<{ frequency: number; q: number; gain: number }> {
  return [
    { frequency: 260 + mouthY * 620 + mouthX * 120, q: 4.5 + mouthY * 6, gain: 0.32 },
    { frequency: 780 + mouthX * 1550 + mouthY * 420, q: 5.5 + mouthX * 7, gain: 0.24 },
    { frequency: 1800 + mouthX * 2300 + mouthY * 1200, q: 3.2 + mouthY * 5, gain: 0.18 }
  ];
}

export function renderYidakiDrone(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("yidaki_didgeridoo", input.variantId);
  const mouthX = controlNumber(input, "mouthX", controlNumber(input, "breathPressure", 0.42));
  const mouthY = controlNumber(input, "mouthY", controlNumber(input, "mouthShapeY", 0.36));
  const pressure = controlNumber(input, "droneLevel", 0.72);
  const vocalization = controlNumber(input, "vocalization", 0.28);
  const circularHold = input.controls.circularHold === true;
  const gain = context.createGain();
  const reedBus = context.createGain();
  const formantBus = context.createGain();
  const reedOsc = context.createOscillator();
  const subOsc = context.createOscillator();
  const pressureLfo = context.createOscillator();
  const pressureLfoGain = context.createGain();
  const lipReed = makeLipReedCurve(context, pressure);
  const lowpass = context.createBiquadFilter();
  const vocalOsc = context.createOscillator();
  const ringGain = context.createGain();
  const sources: AudioScheduledSourceNode[] = [reedOsc, subOsc, pressureLfo, vocalOsc];
  const cleanupNodes: AudioNode[] = [gain, reedBus, formantBus, pressureLfoGain, lipReed, lowpass, ringGain];
  const baseFrequency = clamp(input.frequency, 54, 86);
  const breath = createNoiseBurst(context, {
    duration: Math.max(2.2, input.duration ?? 4),
    attack: 0.04,
    decay: circularHold ? 0.18 : 0.5,
    gain: (variant?.noiseGain ?? 0.36) * 0.024 + mouthY * 0.028,
    filterType: "bandpass",
    frequency: 420 + mouthX * 1600,
    q: 1.2 + mouthY * 2,
    seed: hashSeed(input.instrumentId, input.variantId, "drone-breath"),
    tilt: "dark",
    startTime: input.now
  });

  gain.gain.value = 0.0001;
  scheduleSafeEnvelope(gain.gain, context, {
    attack: 0.06 + (variant?.attack ?? 0.5) * 0.05,
    decay: 0.24,
    sustain: circularHold ? 0.94 : 0.82,
    release: 0.82,
    peakGain: input.velocity * (0.34 + pressure * 0.18),
    startTime: input.now
  });
  reedOsc.type = "sawtooth";
  subOsc.type = "triangle";
  reedOsc.frequency.setValueAtTime(baseFrequency * (1 + mouthX * 0.015), input.now);
  subOsc.frequency.setValueAtTime(baseFrequency * 0.5, input.now);
  lowpass.type = "lowpass";
  lowpass.frequency.setValueAtTime(820 + mouthY * 1800 + mouthX * 1300, input.now);
  lowpass.Q.setValueAtTime(0.8 + mouthY * 1.2, input.now);
  reedBus.gain.value = 0.86;
  formantBus.gain.value = 1;

  reedOsc.connect(lipReed);
  subOsc.connect(lipReed);
  lipReed.connect(lowpass);
  lowpass.connect(reedBus);
  reedBus.connect(formantBus);
  breath.output.connect(formantBus);
  cleanupNodes.push(...breath.cleanupNodes);
  sources.push(breath.source);
  cleanupNodes.push(...createBodyResonatorBank(context, formantBus, gain, boreResonators(baseFrequency, input.variantId), variant?.bodyGain ?? 0.74));

  for (const formant of formantFrequencies(mouthX, mouthY)) {
    const filter = context.createBiquadFilter();
    const formantGain = context.createGain();

    filter.type = "bandpass";
    filter.frequency.setValueAtTime(formant.frequency, input.now);
    filter.Q.setValueAtTime(formant.q, input.now);
    formantGain.gain.value = formant.gain;
    formantBus.connect(filter);
    filter.connect(formantGain);
    formantGain.connect(gain);
    
    // Add continuous slow formant movement (throat/mouth morphing)
    const sweepLfo = context.createOscillator();
    const sweepGain = context.createGain();
    sweepLfo.type = "sine";
    sweepLfo.frequency.value = 0.2 + Math.random() * 0.4; // 0.2 to 0.6 Hz morphing
    sweepGain.gain.value = formant.frequency * 0.14; // 14% sweep depth
    
    sweepLfo.connect(sweepGain);
    sweepGain.connect(filter.frequency);
    sweepLfo.start(input.now);
    sources.push(sweepLfo);
    
    cleanupNodes.push(filter, formantGain, sweepGain);

    if (input.ornament === "tongue_pulse" || input.ornament === "drone_accent") {
      filter.frequency.linearRampToValueAtTime(formant.frequency * (1.12 + mouthX * 0.18), input.now + 0.08);
      filter.frequency.linearRampToValueAtTime(formant.frequency, input.now + 0.18);
      formantGain.gain.setValueAtTime(formant.gain * 0.6, input.now);
      formantGain.gain.linearRampToValueAtTime(formant.gain * 1.45, input.now + 0.055);
      formantGain.gain.linearRampToValueAtTime(formant.gain, input.now + 0.2);
    }
  }

  pressureLfo.type = "sine";
  pressureLfo.frequency.setValueAtTime(circularHold ? 0.85 : 1.7, input.now);
  pressureLfoGain.gain.setValueAtTime(circularHold ? 0.018 : 0.028, input.now);
  pressureLfo.connect(pressureLfoGain);
  pressureLfoGain.connect(gain.gain);

  vocalOsc.type = "sine";
  vocalOsc.frequency.setValueAtTime(baseFrequency * (2.6 + mouthY * 1.8), input.now);
  ringGain.gain.setValueAtTime(vocalization * 0.08, input.now);
  reedBus.connect(ringGain);
  vocalOsc.connect(ringGain.gain);
  ringGain.connect(gain);

  reedOsc.start(input.now);
  subOsc.start(input.now);
  pressureLfo.start(input.now);
  vocalOsc.start(input.now);
  breath.source.start(input.now);

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.8,
    debug: {
      family: "lip_reed_drone",
      model: "lip_reed_bore_vocal_tract",
      mouthX,
      mouthY,
      formants: formantFrequencies(mouthX, mouthY).map((formant) => Math.round(formant.frequency)),
      variantTags: variant?.metadataTags,
      layers: ["lip_reed", "bore_resonance", "three_formants", "ring_mod_vocalization", "pressure_fluctuation"]
    }
  });
}
